import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Clipboard } from '@angular/cdk/clipboard';
import { ChartType } from 'angular-google-charts';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';

export interface FibuCheckArtikel {
    artikel_id: number;
    bezeichnung: string;
    kuerzel: string;
    buchungskonto: string;
    buchungsgegenkonto: string;
    warengruppe: string;
    tags: string;
    tag_count: number;
    lieferant_links: number;
    konten_treffer: number;
    positionen_count: number;
    positionen_90d: number;
    netto_90d: number;
    netto_365d: number;
    gegenkonto_default?: boolean;
    issues: string[];
    ok: boolean;
}

export interface FibuCheckMismatch {
    kosten_id: number;
    position: number;
    artikel: string;
    artikel_buchungskonto: string;
    position_sachkonto?: string;
    position_konto_nr?: string;
    kosten_netto: number;
    rechnung_nr?: string;
    datum?: string | Date;
    lieferant?: string;
}

export interface FibuCheckKonto {
    konto_nr: string;
    bezeichnung: string;
    sachkonto?: string;
    kostenstelle_suffix?: string;
    konto_typ?: number | string;
}

export interface FibuMonthly {
    monatz: string;
    belege: number;
    positionen: number;
    netto: number;
}

export interface FibuUsageRow {
    konto_nr?: string;
    base_konto?: string;
    lieferant_id?: number;
    lieferant?: string;
    kreditor_nr?: string;
    buchungsgegenkonto?: string;
    artikel_id?: number;
    artikel?: string;
    buchungskonto?: string;
    positionen: number;
    belege: number;
    netto: number;
}

export interface FibuLieferantCheck {
    lieferant_id: number;
    lieferant: string;
    kreditor_nr: string;
    buchungsgegenkonto: string;
    fibu_kreditor_konto_id?: number | null;
    artikel_mappings: number;
    belege_90d: number;
    netto_90d: number;
    issues: string[];
    ok: boolean;
}

export interface FibuPositionQuality {
    ohne_artikel: number;
    ohne_aufwand_konto: number;
    positionen_90d: number;
    belege_90d: number;
    freigegeben_90d: number;
    offen_90d: number;
    soll_netto_90d: number;
    haben_netto_90d: number;
}

export interface FibuOhneAufwandRow {
    artikel_id?: number;
    artikel?: string;
    buchungskonto?: string;
    lieferant_id?: number;
    lieferant?: string;
    positionen: number;
    netto: number;
}

export interface FibuDuplicateKonto {
    buchungskonto: string;
    artikel_count: number;
    positionen_90d: number;
    netto_90d: number;
    artikel: { artikel_id: number; bezeichnung: string; positionen_90d: number; netto_90d: number }[];
}

export interface FibuWarengruppe {
    warengruppe: string;
    artikel_count: number;
    ok: number;
    issues: number;
    positionen_90d: number;
    netto_90d: number;
}

export interface FibuTagStat {
    tag: string;
    artikel_count: number;
    avg_gewicht: number;
}

export interface FibuCheckResponse {
    mandant_id: number;
    generated_at: string;
    features: Record<string, boolean>;
    summary: Record<string, number>;
    artikel: FibuCheckArtikel[];
    konto_mismatches: FibuCheckMismatch[];
    aufwand_konten_sample: FibuCheckKonto[];
    monthly_trend: FibuMonthly[];
    usage_by_konto: FibuUsageRow[];
    usage_by_lieferant: FibuUsageRow[];
    usage_by_artikel: FibuUsageRow[];
    position_quality: FibuPositionQuality;
    ohne_aufwand_by_artikel: FibuOhneAufwandRow[];
    ohne_aufwand_by_lieferant: FibuOhneAufwandRow[];
    lieferanten: FibuLieferantCheck[];
    top_tags: FibuTagStat[];
    duplicate_konten: FibuDuplicateKonto[];
    warengruppen: FibuWarengruppe[];
    unmatched_rahmen_konten: FibuCheckKonto[];
}

export interface FibuActionItem {
    severity: 'critical' | 'high' | 'medium' | 'low';
    title: string;
    detail: string;
    issueCode?: string;
    count: number;
    artikelIds: number[];
    tab?: TabId;
}

export interface KontoGroup {
    baseKonto: string;
    artikelCount: number;
    okCount: number;
    issueCount: number;
    positionen90d: number;
    netto90d: number;
    artikel: FibuCheckArtikel[];
}

export interface MismatchGroup {
    artikel: string;
    artikelKonto: string;
    count: number;
    nettoSum: number;
    rows: FibuCheckMismatch[];
}

type TabId = 'analyse' | 'volumen' | 'artikel' | 'lieferanten' | 'abweichungen' | 'konten' | 'qualitaet';
type ArtikelFilter =
    | 'all'
    | 'issues'
    | 'ok'
    | 'critical'
    | 'kein_buchungskonto'
    | 'konto_nicht_im_rahmen'
    | 'ohne_tags'
    | 'ohne_lieferant_mapping'
    | 'aktiv';

@Component({
    selector: 'kiene-fibu-check',
    templateUrl: './fibu-check.component.html',
    styleUrls: ['./fibu-check.component.scss'],
})
export class FibuCheckComponent implements OnInit {
    @ViewChild('problemsJsonArea') problemsJsonArea?: ElementRef<HTMLTextAreaElement>;

    loading = true;
    data: FibuCheckResponse | null = null;
    tab: TabId = 'analyse';
    filter: ArtikelFilter = 'all';
    searchCtrl = new FormControl('');
    liefSearchCtrl = new FormControl('');
    sortBy: 'severity' | 'name' | 'usage' | 'konto' | 'netto' = 'severity';
    liefFilter: 'all' | 'issues' | 'ok' = 'all';
    showProblemsJson = false;
    problemsJson = '';

    issueLabels: Record<string, string> = {
        kein_buchungskonto: 'Kein Buchungskonto',
        konto_nicht_im_rahmen: 'Konto nicht im FiBu-Rahmen',
        ohne_tags: 'Ohne Tags',
        ohne_lieferant_mapping: 'Ohne Lieferant-Mapping',
        kein_kreditor: 'Kein Kreditor',
        kein_fibu_kreditor_link: 'Kein FiBu-Kreditor-Link',
        kein_gegenkonto: 'Kein Gegenkonto',
        ohne_artikel_mapping: 'Ohne Artikel-Mapping',
    };

    actions: FibuActionItem[] = [];
    kontoGroups: KontoGroup[] = [];
    mismatchGroups: MismatchGroup[] = [];
    healthScore = 0;
    riskScore = 0;
    expandedMismatch: string | null = null;
    expandedKonto: string | null = null;
    expandedDup: string | null = null;

    chartTypeLine = ChartType.Line;
    chartTypePie = ChartType.PieChart;
    monthlyColumns = ['Monat', 'Netto €'];
    monthlyData: (string | number)[][] = [['', 0]];
    kontoPieColumns = ['Konto', 'Netto'];
    kontoPieData: (string | number)[][] = [['', 0]];
    liefPieColumns = ['Lieferant', 'Netto'];
    liefPieData: (string | number)[][] = [['', 0]];
    issuePieColumns = ['Problem', 'Anzahl'];
    issuePieData: (string | number)[][] = [['', 0]];

    constructor(
        private readonly router: Router,
        private readonly http: HttpClient,
        private readonly localStorage: LocalStorageService,
        private readonly messageService: MessageService,
        private readonly clipboard: Clipboard,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    ngOnInit(): void {
        if (!this.localStorage.currentUserIsAdmin()) {
            this.messageService.infoMessage('Nur für Admins.');
            this.router.navigate(['/']);
            return;
        }
        this.reload();
    }

    reload(): void {
        this.loading = true;
        this.http.get<FibuCheckResponse>(this.apiBaseUrl + 'fibu_check/read.php').subscribe({
            next: res => {
                this.data = res;
                this.buildAnalysis();
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.messageService.errorMessage('FiBu-Check konnte nicht geladen werden.', err);
            },
        });
    }

    toggleProblemsJson(): void {
        if (!this.data) {
            return;
        }
        this.showProblemsJson = !this.showProblemsJson;
        if (this.showProblemsJson) {
            this.problemsJson = JSON.stringify(this.buildProblemsPayload(), null, 2);
            setTimeout(() => this.selectProblemsJson(), 0);
        }
    }

    selectProblemsJson(): void {
        const el = this.problemsJsonArea?.nativeElement;
        if (!el) {
            return;
        }
        el.focus();
        el.select();
    }

    /** Kompaktes Problem-JSON für Chat/Support – nur Issues, kein Vollstamm. */
    copyProblemsJson(): void {
        if (!this.data) {
            return;
        }
        if (!this.problemsJson) {
            this.problemsJson = JSON.stringify(this.buildProblemsPayload(), null, 2);
        }
        this.showProblemsJson = true;
        const ok = this.clipboard.copy(this.problemsJson);
        setTimeout(() => this.selectProblemsJson(), 0);
        if (ok) {
            this.messageService.infoMessage('JSON kopiert – kannst du hier einfügen.');
        } else {
            this.messageService.infoMessage('Bitte im Textfeld Strg+C drücken.');
        }
    }

    private buildProblemsPayload(): Record<string, unknown> {
        const d = this.data!;
        const artikelIssues = (d.artikel ?? [])
            .filter(a => !a.ok)
            .map(a => ({
                artikel_id: a.artikel_id,
                bezeichnung: a.bezeichnung,
                kuerzel: a.kuerzel,
                buchungskonto: a.buchungskonto || null,
                warengruppe: a.warengruppe || null,
                issues: a.issues,
                tag_count: a.tag_count,
                lieferant_links: a.lieferant_links,
                konten_treffer: a.konten_treffer,
                positionen_90d: a.positionen_90d,
                netto_90d: a.netto_90d,
                kritisch:
                    a.positionen_90d > 0 &&
                    (a.issues.includes('kein_buchungskonto') || a.issues.includes('konto_nicht_im_rahmen')),
            }))
            .sort((a, b) => Number(b.kritisch) - Number(a.kritisch) || b.netto_90d - a.netto_90d);

        const lieferantenIssues = (d.lieferanten ?? [])
            .filter(l => !l.ok)
            .map(l => ({
                lieferant_id: l.lieferant_id,
                lieferant: l.lieferant,
                kreditor_nr: l.kreditor_nr || null,
                buchungsgegenkonto: l.buchungsgegenkonto || null,
                artikel_mappings: l.artikel_mappings,
                belege_90d: l.belege_90d,
                netto_90d: l.netto_90d,
                issues: l.issues,
            }));

        const pq = d.position_quality;
        return {
            source: 'fibu-check',
            mandant_id: d.mandant_id,
            generated_at: d.generated_at,
            health_score: this.healthScore,
            risk_score: this.riskScore,
            summary: {
                artikel_gesamt: d.summary?.artikel_gesamt ?? 0,
                ok: d.summary?.ok ?? 0,
                kein_buchungskonto: d.summary?.kein_buchungskonto ?? 0,
                konto_nicht_im_rahmen: d.summary?.konto_nicht_im_rahmen ?? 0,
                ohne_tags: d.summary?.ohne_tags ?? 0,
                ohne_lieferant_mapping: d.summary?.ohne_lieferant_mapping ?? 0,
                kritisch_genutzt: d.summary?.kritisch_genutzt ?? 0,
                mit_positionen_90d: d.summary?.mit_positionen_90d ?? 0,
                netto_90d: d.summary?.netto_90d ?? 0,
            },
            actions: this.actions.map(a => ({
                severity: a.severity,
                title: a.title,
                detail: a.detail,
                count: a.count,
                issueCode: a.issueCode || null,
                tab: a.tab || null,
            })),
            artikel_mit_problemen: artikelIssues,
            lieferanten_mit_problemen: lieferantenIssues,
            konto_abweichungen: (d.konto_mismatches ?? []).map(m => ({
                kosten_id: m.kosten_id,
                position: m.position,
                artikel: m.artikel,
                artikel_buchungskonto: m.artikel_buchungskonto,
                position_sachkonto: m.position_sachkonto || null,
                position_konto_nr: m.position_konto_nr || null,
                kosten_netto: m.kosten_netto,
                lieferant: m.lieferant || null,
                rechnung_nr: m.rechnung_nr || null,
                datum: m.datum || null,
            })),
            position_quality: pq
                ? {
                      ohne_artikel: pq.ohne_artikel,
                      ohne_aufwand_konto: pq.ohne_aufwand_konto,
                      belege_90d: pq.belege_90d,
                      offen_90d: pq.offen_90d,
                      freigegeben_90d: pq.freigegeben_90d,
                  }
                : null,
            ohne_aufwand_by_artikel: d.ohne_aufwand_by_artikel ?? [],
            ohne_aufwand_by_lieferant: d.ohne_aufwand_by_lieferant ?? [],
            duplicate_konten: (d.duplicate_konten ?? []).map(x => ({
                buchungskonto: x.buchungskonto,
                artikel_count: x.artikel_count,
                positionen_90d: x.positionen_90d,
                netto_90d: x.netto_90d,
                artikel: x.artikel,
            })),
            unmatched_rahmen_konten: (d.unmatched_rahmen_konten ?? []).map(k => ({
                konto_nr: k.konto_nr,
                bezeichnung: k.bezeichnung,
            })),
        };
    }

    private buildAnalysis(): void {
        const artikel = this.data?.artikel ?? [];
        const mismatches = this.data?.konto_mismatches ?? [];
        const total = artikel.length || 1;
        const ok = artikel.filter(a => a.ok).length;
        this.healthScore = Math.round((ok / total) * 100);

        const krit = Number(this.data?.summary?.kritisch_genutzt || 0);
        const aktiv = Number(this.data?.summary?.mit_positionen_90d || 0) || 1;
        this.riskScore = Math.min(100, Math.round((krit / aktiv) * 100 + (mismatches.length > 0 ? 10 : 0)));

        this.actions = this.buildActions(artikel, mismatches);
        this.kontoGroups = this.buildKontoGroups(artikel);
        this.mismatchGroups = this.buildMismatchGroups(mismatches);
        this.buildCharts();
    }

    private buildCharts(): void {
        const monthly = this.data?.monthly_trend ?? [];
        this.monthlyData = monthly.length
            ? monthly.map(m => [m.monatz, Number(m.netto) || 0])
            : [['keine Daten', 0]];

        const byKonto = this.data?.usage_by_konto ?? [];
        this.kontoPieData = byKonto.length
            ? byKonto.slice(0, 12).map(k => [String(k.konto_nr || '?'), Number(k.netto) || 0])
            : [['keine Daten', 0]];

        const byLief = this.data?.usage_by_lieferant ?? [];
        this.liefPieData = byLief.length
            ? byLief.slice(0, 12).map(l => [String(l.lieferant || '?').slice(0, 28), Number(l.netto) || 0])
            : [['keine Daten', 0]];

        const s = this.data?.summary;
        this.issuePieData = s
            ? [
                  ['OK', Number(s.ok) || 0],
                  ['Kein Konto', Number(s.kein_buchungskonto) || 0],
                  ['Nicht im Rahmen', Number(s.konto_nicht_im_rahmen) || 0],
                  ['Ohne Tags', Number(s.ohne_tags) || 0],
                  ['Ohne Lief.-Map', Number(s.ohne_lieferant_mapping) || 0],
              ].filter(r => Number(r[1]) > 0)
            : [['keine Daten', 0]];
        if (!this.issuePieData.length) {
            this.issuePieData = [['keine Daten', 0]];
        }
    }

    private buildActions(artikel: FibuCheckArtikel[], mismatches: FibuCheckMismatch[]): FibuActionItem[] {
        const actions: FibuActionItem[] = [];
        const pq = this.data?.position_quality;

        const noKontoUsed = artikel.filter(
            a => a.issues.includes('kein_buchungskonto') && a.positionen_90d > 0,
        );
        if (noKontoUsed.length) {
            actions.push({
                severity: 'critical',
                title: 'Genutzte Artikel ohne Buchungskonto',
                detail: `Σ ${this.sumNetto(noKontoUsed)} € netto (90d) – Priorität 1.`,
                issueCode: 'kein_buchungskonto',
                count: noKontoUsed.length,
                artikelIds: noKontoUsed.map(a => a.artikel_id),
            });
        }

        const missingInRahmenUsed = artikel.filter(
            a => a.issues.includes('konto_nicht_im_rahmen') && a.positionen_90d > 0,
        );
        if (missingInRahmenUsed.length) {
            actions.push({
                severity: 'critical',
                title: 'Genutzte Artikel: Konto fehlt im FiBu-Rahmen',
                detail: `Σ ${this.sumNetto(missingInRahmenUsed)} € – Match/Buchung riskant.`,
                issueCode: 'konto_nicht_im_rahmen',
                count: missingInRahmenUsed.length,
                artikelIds: missingInRahmenUsed.map(a => a.artikel_id),
            });
        }

        if (pq && pq.ohne_artikel > 0) {
            actions.push({
                severity: 'critical',
                title: 'Positionen ohne Artikel',
                detail: `${pq.ohne_artikel} Pos. in 90 Tagen ohne Artikelzuordnung.`,
                count: pq.ohne_artikel,
                artikelIds: [],
                tab: 'qualitaet',
            });
        }

        if (pq && pq.ohne_aufwand_konto > 0) {
            actions.push({
                severity: 'high',
                title: 'Positionen ohne Aufwandskonto',
                detail: `${pq.ohne_aufwand_konto} Pos. ohne fibu_aufwand_konto_id (90d).`,
                count: pq.ohne_aufwand_konto,
                artikelIds: [],
                tab: 'qualitaet',
            });
        }

        if (mismatches.length) {
            const netto = mismatches.reduce((s, m) => s + (Number(m.kosten_netto) || 0), 0);
            actions.push({
                severity: 'high',
                title: 'Konto-Abweichungen an Positionen',
                detail: `${mismatches.length} Pos. (180 Tage), Netto Σ ${netto.toFixed(2)} €.`,
                count: mismatches.length,
                artikelIds: [],
                tab: 'abweichungen',
            });
        }

        const liefIssues = (this.data?.lieferanten ?? []).filter(l => !l.ok && l.belege_90d > 0);
        if (liefIssues.length) {
            actions.push({
                severity: 'high',
                title: 'Aktive Lieferanten mit Stammdaten-Lücken',
                detail: 'Kreditor / Gegenkonto / Artikel-Mapping prüfen.',
                count: liefIssues.length,
                artikelIds: [],
                tab: 'lieferanten',
            });
        }

        const noKontoIdle = artikel.filter(
            a => a.issues.includes('kein_buchungskonto') && a.positionen_90d <= 0,
        );
        if (noKontoIdle.length) {
            actions.push({
                severity: 'high',
                title: 'Artikel ohne Buchungskonto (ungenutzt)',
                detail: 'Stamm unvollständig – vor erstem Scan pflegen.',
                issueCode: 'kein_buchungskonto',
                count: noKontoIdle.length,
                artikelIds: noKontoIdle.map(a => a.artikel_id),
            });
        }

        const noTagsUsed = artikel.filter(a => a.issues.includes('ohne_tags') && a.positionen_90d > 0);
        if (noTagsUsed.length) {
            actions.push({
                severity: 'medium',
                title: 'Genutzte Artikel ohne Tags',
                detail: `Σ ${this.sumNetto(noTagsUsed)} € – Invoice-Match schwächer.`,
                issueCode: 'ohne_tags',
                count: noTagsUsed.length,
                artikelIds: noTagsUsed.map(a => a.artikel_id),
            });
        }

        const dups = this.data?.duplicate_konten ?? [];
        if (dups.length) {
            actions.push({
                severity: 'medium',
                title: 'Mehrere Artikel teilen sich ein Konto',
                detail: `${dups.length} Konten mit ≥2 Artikeln – oft gewollt, aber prüfen.`,
                count: dups.length,
                artikelIds: [],
                tab: 'qualitaet',
            });
        }

        const noLiefUsed = artikel.filter(
            a => a.issues.includes('ohne_lieferant_mapping') && a.positionen_90d > 0,
        );
        if (noLiefUsed.length) {
            actions.push({
                severity: 'low',
                title: 'Genutzte Artikel ohne Lieferant-Mapping',
                detail: 'Optional: typische Lieferanten verknüpfen.',
                issueCode: 'ohne_lieferant_mapping',
                count: noLiefUsed.length,
                artikelIds: noLiefUsed.map(a => a.artikel_id),
            });
        }

        const unusedOk = artikel.filter(a => a.ok && a.positionen_count === 0);
        if (unusedOk.length) {
            actions.push({
                severity: 'low',
                title: 'OK aber noch nie gebucht',
                detail: 'Stammdaten stehen, bisher keine Position.',
                count: unusedOk.length,
                artikelIds: unusedOk.map(a => a.artikel_id),
            });
        }

        const order = { critical: 0, high: 1, medium: 2, low: 3 };
        return actions.sort((a, b) => order[a.severity] - order[b.severity]);
    }

    private sumNetto(list: FibuCheckArtikel[]): string {
        return list.reduce((s, a) => s + (Number(a.netto_90d) || 0), 0).toFixed(2);
    }

    private buildKontoGroups(artikel: FibuCheckArtikel[]): KontoGroup[] {
        const map = new Map<string, FibuCheckArtikel[]>();
        for (const a of artikel) {
            const base = this.baseKonto(a.buchungskonto) || '(kein Konto)';
            if (!map.has(base)) {
                map.set(base, []);
            }
            map.get(base)!.push(a);
        }
        return Array.from(map.entries())
            .map(([baseKonto, list]) => ({
                baseKonto,
                artikelCount: list.length,
                okCount: list.filter(x => x.ok).length,
                issueCount: list.filter(x => !x.ok).length,
                positionen90d: list.reduce((s, x) => s + (x.positionen_90d || 0), 0),
                netto90d: list.reduce((s, x) => s + (Number(x.netto_90d) || 0), 0),
                artikel: list.sort((a, b) => (b.netto_90d || 0) - (a.netto_90d || 0)),
            }))
            .sort((a, b) => b.netto90d - a.netto90d || b.positionen90d - a.positionen90d);
    }

    private buildMismatchGroups(rows: FibuCheckMismatch[]): MismatchGroup[] {
        const map = new Map<string, FibuCheckMismatch[]>();
        for (const r of rows) {
            const key = `${r.artikel || '?'}|${r.artikel_buchungskonto || ''}`;
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key)!.push(r);
        }
        return Array.from(map.entries())
            .map(([, list]) => ({
                artikel: String(list[0].artikel || '—'),
                artikelKonto: String(list[0].artikel_buchungskonto || '—'),
                count: list.length,
                nettoSum: list.reduce((s, m) => s + (Number(m.kosten_netto) || 0), 0),
                rows: list,
            }))
            .sort((a, b) => b.count - a.count || b.nettoSum - a.nettoSum);
    }

    baseKonto(konto: string | null | undefined): string {
        const k = (konto || '').trim();
        if (!k) {
            return '';
        }
        return k.includes('.') ? k.split('.')[0] : k;
    }

    setFilter(f: ArtikelFilter): void {
        this.filter = f;
        this.tab = 'artikel';
    }

    openAction(action: FibuActionItem): void {
        if (action.tab) {
            this.tab = action.tab;
            return;
        }
        if (action.issueCode) {
            this.setFilter(action.issueCode as ArtikelFilter);
            return;
        }
        this.setFilter('issues');
    }

    toggleMismatch(key: string): void {
        this.expandedMismatch = this.expandedMismatch === key ? null : key;
    }

    toggleKonto(key: string): void {
        this.expandedKonto = this.expandedKonto === key ? null : key;
    }

    toggleDup(key: string): void {
        this.expandedDup = this.expandedDup === key ? null : key;
    }

    openKontoGroup(baseKonto: string): void {
        this.tab = 'konten';
        this.expandedKonto = baseKonto;
    }

    mismatchKey(g: MismatchGroup): string {
        return g.artikel + '|' + g.artikelKonto;
    }

    get filteredArtikel(): FibuCheckArtikel[] {
        let list = this.data?.artikel ?? [];
        const q = (this.searchCtrl.value || '').trim().toLowerCase();
        if (q) {
            list = list.filter(
                a =>
                    String(a.artikel_id).includes(q) ||
                    (a.bezeichnung || '').toLowerCase().includes(q) ||
                    (a.kuerzel || '').toLowerCase().includes(q) ||
                    (a.buchungskonto || '').toLowerCase().includes(q) ||
                    (a.warengruppe || '').toLowerCase().includes(q) ||
                    (a.tags || '').toLowerCase().includes(q),
            );
        }
        if (this.filter === 'ok') {
            list = list.filter(a => a.ok);
        } else if (this.filter === 'issues') {
            list = list.filter(a => !a.ok);
        } else if (this.filter === 'aktiv') {
            list = list.filter(a => a.positionen_90d > 0);
        } else if (this.filter === 'critical') {
            list = list.filter(
                a =>
                    a.positionen_90d > 0 &&
                    (a.issues.includes('kein_buchungskonto') || a.issues.includes('konto_nicht_im_rahmen')),
            );
        } else if (this.filter !== 'all') {
            list = list.filter(a => a.issues.includes(this.filter));
        }

        const sev = (a: FibuCheckArtikel) => {
            if (a.ok) {
                return 9;
            }
            if (
                a.positionen_90d > 0 &&
                (a.issues.includes('kein_buchungskonto') || a.issues.includes('konto_nicht_im_rahmen'))
            ) {
                return 0;
            }
            if (a.issues.includes('kein_buchungskonto') || a.issues.includes('konto_nicht_im_rahmen')) {
                return 1;
            }
            if (a.issues.includes('ohne_tags')) {
                return 2;
            }
            return 3;
        };

        return [...list].sort((a, b) => {
            if (this.sortBy === 'name') {
                return (a.bezeichnung || '').localeCompare(b.bezeichnung || '', 'de');
            }
            if (this.sortBy === 'usage') {
                return (b.positionen_90d || 0) - (a.positionen_90d || 0);
            }
            if (this.sortBy === 'netto') {
                return (b.netto_90d || 0) - (a.netto_90d || 0);
            }
            if (this.sortBy === 'konto') {
                return (a.buchungskonto || '').localeCompare(b.buchungskonto || '', 'de');
            }
            return sev(a) - sev(b) || (b.netto_90d || 0) - (a.netto_90d || 0);
        });
    }

    get filteredLieferanten(): FibuLieferantCheck[] {
        let list = this.data?.lieferanten ?? [];
        const q = (this.liefSearchCtrl.value || '').trim().toLowerCase();
        if (q) {
            list = list.filter(
                l =>
                    String(l.lieferant_id).includes(q) ||
                    (l.lieferant || '').toLowerCase().includes(q) ||
                    (l.kreditor_nr || '').toLowerCase().includes(q) ||
                    (l.buchungsgegenkonto || '').toLowerCase().includes(q),
            );
        }
        if (this.liefFilter === 'ok') {
            list = list.filter(l => l.ok);
        } else if (this.liefFilter === 'issues') {
            list = list.filter(l => !l.ok);
        }
        return list;
    }

    issueLabel(code: string): string {
        return this.issueLabels[code] || code;
    }

    severityClass(s: FibuActionItem['severity']): string {
        return 'sev-' + s;
    }

    euro(n: number | null | undefined): string {
        return (Number(n) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
}
